// English translations for the business management application
export default {
  // Landing page translations
  landing: {
    hero: {
      badge: 'Building for Indian SMEs',
      title: 'Run your business.',
      subtitle: 'Not paperwork.',
      description: 'The upcoming enterprise OS for Indian SMEs. Streamline GST, inventory, and trade finance with local-first luxury.',
      cta_waitlist: 'Join Waitlist',
      cta_demo: 'Watch Architecture',
      trust_early: 'Early Access Soon',
      trust_card: 'No Credit Card',
      trust_gst: 'GST Focused',
      stats_revenue: 'Revenue',
      stats_inventory: 'Inventory'
    },
    social_proof: {
      stats: {
        smes: { value: 'Indian SMEs', label: 'Building For' },
        gst: { value: 'GST Focused', label: 'Compliance' },
        cloud: { value: 'Cloud Native', label: 'Architecture' },
        status: { value: 'Early Access', label: 'Status' }
      }
    },
    problem: {
      badge: 'Critical Challenges',
      title: 'The friction holding your',
      subtitle: 'scale back.',
      description: "The modern Indian business landscape is evolving rapidly. Legacy tools weren't built for the speed of digital commerce and GST complexity.",
      cards: {
        paperwork: {
          title: 'Hours lost to paperwork',
          description: 'Manual invoicing, data entry, and reconciliation eat up 15+ hours every week. Time you should be spending on growth.'
        },
        gst: {
          title: 'GST compliance headaches',
          description: 'Calculating correct tax rates, filing returns on time, and avoiding penalties is a constant technical and mental drain.'
        },
        inventory: {
          title: 'Inventory blind spots',
          description: "Stockouts and overstocking happen because you don't have real-time visibility across your entire warehouse network."
        }
      }
    },
    features: {
      title: 'Enterprise power.',
      subtitle: 'Simplified for you.',
      description: 'Soma combines industrial-grade accounting with a seamless interface designed for the modern business owner.',
      cards: {
        invoicing: {
          title: 'GST Invoicing',
          description: 'Designed to create GST-compliant invoices in seconds. Auto-calculate tax rates with accurate local compliance.'
        },
        inventory: {
          title: 'Inventory Sync',
          description: 'Track real-time stock levels across multi-city warehouses. Predictive alerts on inventory health.'
        },
        intelligence: {
          title: 'Trade Intelligence',
          description: 'Generate sophisticated P&L, cash flow, and tax reports with our local architecture.'
        },
        mobile: {
          title: 'Unified Mobile',
          description: 'Manage your entire enterprise from your phone. Enterprise power, consumer simplicity.'
        }
      },
      learn_more: 'Learn More'
    },
    cta: {
      badge: 'Pre-Launch Access',
      title: 'The future of',
      subtitle: 'Indian trade.',
      description: "Be among the first to experience Soma's enterprise architecture. Built for speed, scale, and local compliance.",
      btn_waitlist: 'Join Waitlist',
      btn_features: 'View Features',
      benefits: {
        early_access: 'Early Access',
        priority: 'Priority Onboarding',
        updates: 'Updates Included'
      }
    },
    differentiation: {
      badge: 'Omnichannel Architecture',
      title: 'Trade from',
      subtitle: 'anywhere.',
      description: 'Designed so you can start an invoice on your phone while talking to a customer, and finish it on your laptop later. Data will sync instantly across all devices, even in low-connectivity areas.',
      items: {
        mobile: {
          title: 'Mobile First',
          desc: 'Tailored for high-speed trade in the Indian marketplace.'
        },
        desktop: {
          title: 'Desktop Scale',
          desc: 'Massive inventory operations and bulk trade clearing.'
        },
        sync: {
          title: 'Offline Sync',
          desc: 'Data persists locally. Sync happens as soon as you connect.'
        }
      },
      stats: {
        latency: { value: '< 1s', label: 'Target Latency' },
        uptime: { value: '99.9%', label: 'Target Uptime' },
        crypto: { value: 'AES-256', label: 'Crypto' }
      },
      sync_status: 'Live Sync'
    },
    faq: {
      badge: 'Knowledge Base',
      title: 'Clarifying',
      subtitle: 'the details.',
      questions: {
        setup: {
          q: 'How quickly will I be able to set up Soma once launched?',
          a: 'Soma is designed to get you fully set up in under 2 minutes. Just add your business name and GST number, and you can begin creating invoices immediately. We also plan to offer free migration assistance from Tally, Zoho, or spreadsheets.'
        },
        security: {
          q: 'Is my business data safe and secure?',
          a: 'Absolutely. We are implementing bank-grade 256-bit encryption for all data. Your information will be stored on secure servers in India with multiple backups. We are building to be ISO 27001 certified and fully DPDP compliant.'
        },
        knowledge: {
          q: 'Do I need any accounting or technical knowledge?',
          a: 'Not at all. Soma is designed for business owners, not accountants. If you can use WhatsApp, you will be able to use Soma. We handle all the complex GST calculations, tax categorizations, and compliance requirements automatically.'
        },
        offline: {
          q: 'What happens if I don\'t have an internet connection?',
          a: 'Soma is designed to work offline too. You can continue creating invoices, updating inventory, and recording sales on our mobile app. Once you\'re back online, everything syncs automatically across all your devices.'
        },
        migration: {
          q: 'Can I migrate my existing data from Tally?',
          a: 'Yes! We are building import tools to handle your customer data, product catalog, and historical transactions from Tally, Zoho Books, or Excel spreadsheets seamlessly.'
        }
      },
      cta: {
        title: 'Architectural Support',
        link: 'Contact our engineers'
      }
    },
    testimonials: {
      badge: 'Our Focus',
      title: 'Designing for every',
      subtitle: 'Indian business.',
      description: 'From wholesalers to manufacturers, Soma is being designed to adapt to your unique trade requirements.',
      featured: {
        title: 'Wholesale & Distribution',
        description: 'Manage bulk inventory, complex pricing tiers, and credit cycles with ease. Soma is built to handle the volume so you can focus on the trade.',
        badge: {
          title: 'Wholesale',
          desc: 'High Volume • Low Margin'
        },
        stats: {
          inventory: { label: 'Inventory', value: 'Batch Tracking' },
          pricing: { label: 'Pricing', value: 'Multi-Tier' },
          finance: { label: 'Finance', value: 'Credit Mgmt' }
        }
      },
      industries: {
        manufacturing: {
          title: 'Manufacturing',
          description: 'Targeting accurate tracking of raw materials, bill of materials (BOM), and production costs.'
        },
        retail: {
          title: 'Retail Chains',
          description: 'Planned unified point of sale (POS), barcode scanning, and real-time multi-store synchronization.'
        },
        service: {
          title: 'Service Providers',
          description: 'Designed for project-based billing, time tracking, and automated expense management.'
        }
      }
    },
  },
  features_page: {
      badge: 'SME OS Core Features',
      title: 'Built for the',
      subtitle: 'Unstoppable SME.',
      description: 'Soma is being built from the ground up to solve the complexities of modern SMEs. Automate GST, track inventory, and get real-time insights in one platform.',
      cta_waitlist: 'Join Waitlist',
      cta_learn_more: 'Learn More',
      features: {
        gst: {
          title: 'GST Compliance',
          description: 'Automated GST filing, invoice generation, and compliance tracking tailored for the Indian tax ecosystem.',
          benefits: {
            filing: 'Automated GSTR filing',
            alerts: 'Real-time compliance alerts',
            validation: 'Invoice validation'
          }
        },
        inventory: {
          title: 'Inventory Sync',
          description: 'Real-time inventory tracking across multiple locations with smart reorder points and analytics.',
          benefits: {
            tracking: 'Multi-location tracking',
            alerts: 'Automated reorder alerts',
            analytics: 'Stock movement analytics'
          }
        },
        analytics: {
          title: 'Smart Analytics',
          description: 'Gain deep insights into your business performance with automated P&L and cash flow reports.',
          benefits: {
            pl: 'P&L statements',
            cashflow: 'Cash flow analysis',
            tax: 'Tax optimization insights'
          }
        },
        crm: {
          title: 'Customer CRM',
          description: 'Build lasting relationships with your customers through history tracking and smart engagement.',
          benefits: {
            profiles: 'Customer profiles',
            history: 'Order history',
            logs: 'Communication logs'
          }
        },
        global: {
          title: 'Global Commerce',
          description: 'Expand globally with multi-currency support and FEMA-compliant invoicing workflows.',
          benefits: {
            rates: 'Real-time exchange rates',
            invoicing: 'Multi-currency invoicing',
            compliance: 'FEMA compliance'
          }
        },
        security: {
          title: 'Bank-grade Security',
          description: 'Your business data is protected by the highest encryption standards and local compliance.',
          benefits: {
            encryption: 'End-to-end encryption',
            backups: 'Regular backups',
            compliance: 'Indian DPDP compliance'
          }
        }
      },
      engineered_workflows: 'Engineered Workflows',
      testimonial: {
        quote: '"Finally, a business OS that understands the complexity of Indian trade. We are eagerly waiting to move our operations to Soma."',
        author: 'Ankit Varshney',
        role: 'CEO, Varshney Textiles (Beta Partner)'
      },
      final_cta: {
        title: 'Ready to experience the future?',
        description: 'Be among the first to access the platform that will redefine Indian SME operations. Request early access today.',
        request_access: 'Request Access',
        view_plans: 'View Plans',
        footer: 'Local-First • GST Compliant • Enterprise Scale'
      }
    },
    solutions_page: {
        badge: 'Industry Specific DNA',
        title: 'Custom Made',
        subtitle: 'for Your Scale.',
        description: 'Generic software fails because your business is unique. Soma adapts to your industry specific workflows out of the box.',
        cta_contact: 'Contact Sales',
        cta_case_studies: 'View Case Studies',
        core_workflow: 'Core Workflow Components',
        top_users: 'Top Industry Users',
        validated_results: 'Validated Results',
        deployment: 'Enterprise Deployment',
        built_for_scale: 'Built for complex Indian scale.',
        industries: {
          retail: {
            title: 'Retail & E-commerce',
            description: 'Scale your retail business with unified inventory and multi-channel billing.',
            features: {
              inventory: 'Multi-channel inventory sync',
              pos: 'Point of Sale (POS) integration',
              loyalty: 'Loyalty & Gift cards',
              reordering: 'Automated stock reordering'
            },
            tags: {
              fashion: 'Fashion',
              electronics: 'Electronics',
              chains: 'Retail Chains'
            },
            proof: {
              label: 'Stockout Reduction',
              company: 'Fashion Retail'
            }
          },
          manufacturing: {
            title: 'Manufacturing',
            description: 'Optimize production with smart cost tracking and raw material workflows.',
            features: {
              planning: 'Production planning (MRP)',
              bom: 'BOM management',
              waste: 'Waste & Scrappage tracking',
              labor: 'Labor cost attribution'
            },
            tags: {
              auto: 'Auto Parts',
              textile: 'Textile Mills',
              food: 'Food Tech'
            },
            proof: {
              label: 'Waste Reduction',
              company: 'Manufacturing'
            }
          },
          services: {
            title: 'Services & Agencies',
            description: 'Streamline client billing and project profitability with zero effort.',
            features: {
              accounting: 'Project-based accounting',
              portal: 'Client portal for payments',
              tracking: 'Time tracking to invoice',
              billing: 'Recurring service billing'
            },
            tags: {
              it: 'IT Agencies',
              law: 'Law Firms',
              consulting: 'Consulting'
            },
            proof: {
              label: 'Faster Billing',
              company: 'Service Agency'
            }
          },
          healthcare: {
            title: 'Healthcare',
            description: 'Compliance-ready back-office tools for medical practices and labs.',
            features: {
              inventory: 'Medical inventory control',
              claims: 'Insurance claim tracking',
              billing: 'Patient billing automation',
              reporting: 'Regulatory reporting'
            },
            tags: {
              clinics: 'Clinics',
              pharmacies: 'Pharmacies',
              labs: 'Labs'
            },
            proof: {
              label: 'Billing Accuracy',
              company: 'Healthcare'
            }
          }
        },
        cta_section: {
          title_prefix: 'Ready to find your',
          title_suffix: 'solution DNA?',
          book_audit: 'Book Architecture Audit',
          talk_expert: 'Talk to an Expert',
          footer_text: 'Deploy local. Scale global.'
        }
    },
    pricing_page: {
      badge: 'Transparent Pricing',
      title: 'Simple Scale.',
      subtitle: 'No Complexity.',
      description: 'Choose the perfect plan to grow your business. Dedicated to transparency, equity, and local-first architecture.',
      billing: {
        monthly: 'Monthly',
        yearly: 'Yearly',
        save: 'Save 20%'
      },
      per_month: '/mo',
      yearly_discount: 'Yearly Discount',
      plans: {
        features_title: 'Included Features',
        per_month: '/mo',
        recommended: 'Recommended',
        starter: {
          name: 'Starter',
          description: 'Perfect for small businesses and growing startups',
          cta: 'Join Waitlist',
          features: {
            invoices: 'Up to 100 invoices/month',
            gst: 'Basic GST compliance',
            inventory: 'Single location inventory',
            support: 'Email support',
            reporting: 'Basic reporting',
            mobile: 'Mobile app access'
          }
        },
        professional: {
          name: 'Professional',
          description: 'Ideal for growing businesses with complex needs',
          cta: 'Join Waitlist',
          features: {
            invoices: 'Unlimited invoices',
            gst: 'Advanced GST compliance',
            inventory: 'Multi-location inventory',
            support: 'Priority support',
            analytics: 'Advanced analytics',
            api: 'API access',
            integrations: 'Custom integrations',
            users: 'Multi-user access'
          }
        },
        enterprise: {
          name: 'Enterprise',
          description: 'For large scale businesses with team workflows',
          cta: 'Contact for Early Access',
          features: {
            all_pro: 'Everything in Professional',
            manager: 'Dedicated account manager',
            workflows: 'Custom workflows',
            security: 'Advanced security features',
            whitelabel: 'White-label options',
            access: 'Role-based access',
            reporting: 'Custom reporting',
            deployment: 'On-premise deployment'
          }
        }
      },
      faq: {
        badge: 'FAQ',
        title: 'Frequently Asked Questions',
        subtitle: 'Everything you need to know about billing and pricing.',
        questions: {
          trial: {
            q: 'Will there be a free trial?',
            a: 'Yes! Upon launch, all plans will come with a 14-day free trial. You will get full access to all features so you can see the value Soma brings to your business. No credit card will be required.'
          },
          change: {
            q: 'Can I change plans anytime?',
            a: 'Absolutely. You will be able to upgrade or downgrade your plan at any time. When you upgrade, the new features will be available immediately.'
          },
          payment: {
            q: 'What payment methods will be accepted?',
            a: 'We plan to accept all major credit cards, debit cards, UPI, net banking, and digital wallets popular in India including PhonePe, Paytm, and Google Pay.'
          },
          security: {
            q: 'Is my data secure?',
            a: 'Yes, we use enterprise-grade 256-bit SSL encryption. We are building to be SOC2 compliant and follow all Indian data protection regulations (DPDP) to ensure your business data is safe.'
          }
        }
      },
      final_cta: {
        title: 'Ready to scale?',
        subtitle: 'Start your journey.',
        book_call: 'Book Architecture Audit',
        contact_sales: 'Contact Sales',
        footer: 'Enterprise Grade • GST Ready • Secure'
      }
    },
  resources_page: {
      badge: 'Soma Knowledge Hub',
      title_prefix: 'Learn to',
      title_suffix: 'Thrive.',
      description: 'Exclusive guides, industry insights, and technical documentation to help you build an unstoppable Indian SME.',
      ledger: {
        title: 'The Ledger',
        subtitle: 'A periodical on SME scale and local-first architecture.',
        cta: 'Explore Archive'
      },
      implementation: {
        title_prefix: 'Implementation',
        title_suffix: 'Mastery.',
        description: 'Our architects provide dedicated sessions for large-scale enterprise deployments and custom manufacturing workflows.',
        cta_training: 'Schedule Training',
        cta_support: 'Technical Support',
        footer: 'Architecture • Integration • Scale'
      },
      cards: {
        quick_start: {
          title: "Quick Start Guide",
          description: "Everything you will need to begin your journey with Soma in under 10 minutes.",
          tag: "Essential"
        },
        mastering_gst: {
          title: "Mastering GST",
          description: "Deep dive into GSTR automation, e-way bills, and local tax compliance.",
          tag: "Updated 2026"
        },
        inventory_strategy: {
          title: "Inventory Strategy",
          description: "How V.K. Prints managed 400% growth using Soma's multi-location sync.",
          tag: "Case Study"
        },
        api_docs: {
          title: "API Documentation",
          description: "Build custom integrations and extend Soma's capabilities.",
          tag: "Technical"
        },
        community: {
          title: "Community Forum",
          description: "Connect with other business owners and Soma experts.",
          tag: "External"
        },
        webinar: {
          title: "Webinar: GST 2026",
          description: "Watch the recording of our latest session on new tax rules.",
          tag: "New"
        }
      }
    },
    ca_page: {
    hero: {
      badge: 'For Modern Chartered Accountants',
      title: 'The Operating System',
      subtitle: 'for Your Practice.',
      description: 'Transform your firm from a filing factory into a high-value advisory powerhouse. Manage clients, automate compliance, and unlock real-time financial insights.',
      cta: 'Start Your Digital Practice'
    },
    workflow: {
      title: 'How It Works',
      steps: [
        {
          title: 'Connect Client',
          desc: 'Invite your clients to Soma or link their existing accounts in seconds.'
        },
        {
          title: 'Auto-Sync Data',
          desc: 'Sales, purchases, and banking data flows automatically into your dashboard.'
        },
        {
          title: 'Advisory & Filing',
          desc: 'Review AI-audited returns and file with one click. No manual data entry.'
        }
      ]
    },
    features: {
      title: 'Everything You Need',
      subtitle: 'To Scale Your Firm',
      list: [
        {
          title: 'Client Command Center',
          desc: 'A "Morning Cockpit" view of your entire practice.',
          detail: 'Track filing status, health scores, and pending tasks across all clients instantly from a single dashboard.'
        },
        {
          title: 'Automated Compliance',
          desc: 'Direct integration with GSTN for auto-filing.',
          detail: 'Auto-populate GSTR-1/3B, reconcile ITC in real-time, and track notices with a dedicated tracker.'
        },
        {
          title: 'Document Vault',
          desc: 'Secure, organized storage for every client.',
          detail: 'Permanent files, working papers, and signed returns are just a search away in your secure cloud vault.'
        },
        {
          title: 'Financial Insights',
          desc: 'Provide CFO-level services with automated analytics.',
          detail: 'Go beyond compliance with automated P&L analysis, cashflow forecasting, and risk heatmaps.'
        }
      ],
      cards: {
        clients: {
          title: 'Client Command Center',
          desc: 'A "Morning Cockpit" view of your entire practice. Track filing status, health scores, and pending tasks across all clients instantly.'
        },
        gst: {
          title: 'Automated Compliance',
          desc: 'Direct integration with GSTN. Auto-populate GSTR-1/3B, reconcile ITC in real-time, and track notices with a dedicated tracker.'
        },
        audit: {
          title: 'Document Vault',
          desc: 'Secure, organized storage for every client. Permanent files, working papers, and signed returns are just a search away.'
        },
        insights: {
          title: 'Financial Insights',
          desc: 'Go beyond compliance. Provide CFO-level services with automated P&L analysis, cashflow forecasting, and risk heatmaps.'
        },
        comms: {
          title: 'Client Collaboration',
          desc: 'Stop the WhatsApp chaos. Contextual comment threads on invoices and returns, keeping all communication professional and audit-ready.'
        },
        billing: {
          title: 'Practice Billing',
          desc: 'Automate your own invoicing. Track billable hours, generate retainers, and get paid faster with integrated payment links.'
        }
      }
    },
    compliance: {
      title: 'Compliance. On Autopilot.',
      description: 'Stay ahead of deadlines with a system that knows the rules better than anyone.',
      items: [
        'Live GSTR-2B vs 2A Reconciliation',
        'Automated Late Fee Calculation',
        'HSN/SAC Code Validation',
        'E-Way Bill & E-Invoice Sync',
        'Smart Notice Detection & Alerts'
      ]
    },
    cta: {
      title: 'Ready to modernize your practice?',
      btn: 'Get Started Free'
    }
  },
  // Layout translations
  layout: {
    navbar: {
      login: 'Sign In',
      get_started: 'Start Free',
      dashboard: 'Dashboard',
      links: {
        features: 'Features',
        pricing: 'Pricing',
        solutions: 'Solutions',
        resources: 'Resources',
        ca_services: 'CA Services'
      }
    },
    footer: {
      brand_desc: 'The premium business operating system engineered for the scale of Indian enterprises.',
      engineered_for: 'Engineered for',
      excellence: 'Excellence',
      sections: {
        product: 'Product',
        company: 'Company',
        resources: 'Resources',
        legal: 'Legal'
      },
      links: {
        features: 'Features',
        pricing: 'Pricing',
        solutions: 'Solutions',
        resources: 'Resources',
        about: 'About',
        blog: 'Blog',
        careers: 'Careers',
        contact: 'Contact',
        docs: 'Documentation',
        help: 'Help Center',
        api: 'API Reference',
        status: 'Status',
        privacy: 'Privacy',
        terms: 'Terms',
        security: 'Security'
      },
      copyright: 'Soma Technologies. All rights reserved.'
    }
  },
  // Common UI elements
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    update: 'Update',
    search: 'Search',
    filter: 'Filter',
    import: 'Import',
    print: 'Print',
    download: 'Download',
    upload: 'Upload',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    finish: 'Finish',
    continue: 'Continue',
    skip: 'Skip',
    retry: 'Retry',
    refresh: 'Refresh',
    view: 'View',
    details: 'Details',
    settings: 'Settings',
    help: 'Help',
    about: 'About',
    contact: 'Contact',
    support: 'Support',
    feedback: 'Feedback',
    logout: 'Logout',
    login: 'Login',
    register: 'Register',
    profile: 'Profile',
    account: 'Account',
    dashboard: 'Dashboard',
    home: 'Home',
    menu: 'Menu',
    navigation: 'Navigation',
    sidebar: 'Sidebar',
    header: 'Header',
    footer: 'Footer',
    content: 'Content',
    main: 'Main',
    section: 'Section',
    page: 'Page',
    tab: 'Tab',
    panel: 'Panel',
    modal: 'Modal',
    dialog: 'Dialog',
    form: 'Form',
    field: 'Field',
    input: 'Input',
    select: 'Select',
    option: 'Option',
    checkbox: 'Checkbox',
    radio: 'Radio',
    button: 'Button',
    link: 'Link',
    image: 'Image',
    icon: 'Icon',
    text: 'Text',
    title: 'Title',
    description: 'Description',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    state: 'State',
    country: 'Country',
    zipcode: 'ZIP Code',
    date: 'Date',
    time: 'Time',
    datetime: 'Date & Time',
    status: 'Status',
    type: 'Type',
    category: 'Category',
    priority: 'Priority',
    quantity: 'Quantity',
    price: 'Price',
    amount: 'Amount',
    total: 'Total',
    subtotal: 'Subtotal',
    tax: 'Tax',
    discount: 'Discount',
    currency: 'Currency',
    unit: 'Unit',
    notes: 'Notes',
    comments: 'Comments',
    tags: 'Tags',
    actions: 'Actions',
    options: 'Options',
    preferences: 'Preferences',
    configuration: 'Configuration',
    advanced: 'Advanced',
    basic: 'Basic',
    general: 'General',
    security: 'Security',
    privacy: 'Privacy',
    notifications: 'Notifications',
    alerts: 'Alerts',
    warnings: 'Warnings',
    errors: 'Errors',
    success: 'Success',
    info: 'Information',
    tips: 'Tips',
    guides: 'Guides',
    tutorials: 'Tutorials',
    documentation: 'Documentation',
    faq: 'FAQ',
    terms: 'Terms',
    conditions: 'Conditions',
    policy: 'Policy',
    agreement: 'Agreement',
    license: 'License',
    version: 'Version',
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    approved: 'Approved',
    rejected: 'Rejected',
    draft: 'Draft',
    published: 'Published',
    archived: 'Archived',
    deleted: 'Deleted',
    new: 'New',
    old: 'Old',
    recent: 'Recent',
    latest: 'Latest',
    popular: 'Popular',
    featured: 'Featured',
    recommended: 'Recommended',
    trending: 'Trending',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    urgent: 'Urgent',
    normal: 'Normal',
    critical: 'Critical',
    important: 'Important',
    optional: 'Optional',
    required: 'Required',
    mandatory: 'Mandatory',
    public: 'Public',
    private: 'Private',
    shared: 'Shared',
    personal: 'Personal',
    business: 'Business',
    professional: 'Professional',
    enterprise: 'Enterprise',
    premium: 'Premium',
    free: 'Free',
    paid: 'Paid',
    trial: 'Trial',
    demo: 'Demo',
    beta: 'Beta',
    alpha: 'Alpha',
    stable: 'Stable',
    development: 'Development',
    production: 'Production',
    testing: 'Testing',
    staging: 'Staging',
    maintenance: 'Maintenance',
    backup: 'Backup',
    restore: 'Restore',
    sync: 'Sync',
    migrate: 'Migrate',
    transfer: 'Transfer',
    copy: 'Copy',
    move: 'Move',
    duplicate: 'Duplicate',
    clone: 'Clone',
    merge: 'Merge',
    split: 'Split',
    combine: 'Combine',
    separate: 'Separate',
    group: 'Group',
    ungroup: 'Ungroup',
    sort: 'Sort',
    order: 'Order',
    arrange: 'Arrange',
    organize: 'Organize',
    manage: 'Manage',
    control: 'Control',
    monitor: 'Monitor',
    track: 'Track',
    analyze: 'Analyze',
    report: 'Report',
    statistics: 'Statistics',
    metrics: 'Metrics',
    analytics: 'Analytics',
    insights: 'Insights',
    trends: 'Trends',
    patterns: 'Patterns',
    data: 'Data',
    information: 'Information',
    content: 'Content',
    media: 'Media',
    files: 'Files',
    documents: 'Documents',
    images: 'Images',
    videos: 'Videos',
    audio: 'Audio',
    attachments: 'Attachments',
    downloads: 'Downloads',
    uploads: 'Uploads',
    storage: 'Storage',
    memory: 'Memory',
    cache: 'Cache',
    database: 'Database',
    server: 'Server',
    client: 'Client',
    network: 'Network',
    internet: 'Internet',
    connection: 'Connection',
    bandwidth: 'Bandwidth',
    speed: 'Speed',
    performance: 'Performance',
    optimization: 'Optimization',
    efficiency: 'Efficiency',
    quality: 'Quality',
    reliability: 'Reliability',
    stability: 'Stability',
    safety: 'Safety',
    protection: 'Protection',
    encryption: 'Encryption',
    authentication: 'Authentication',
    authorization: 'Authorization',
    permission: 'Permission',
    access: 'Access',
    rights: 'Rights',
    privileges: 'Privileges',
    roles: 'Roles',
    users: 'Users',
    groups: 'Groups',
    teams: 'Teams',
    organizations: 'Organizations',
    companies: 'Companies',
    departments: 'Departments',
    divisions: 'Divisions',
    branches: 'Branches',
    locations: 'Locations',
    regions: 'Regions',
    countries: 'Countries',
    languages: 'Languages',
    currencies: 'Currencies',
    timezones: 'Timezones',
    formats: 'Formats',
    templates: 'Templates',
    themes: 'Themes',
    styles: 'Styles',
    layouts: 'Layouts',
    designs: 'Designs',
    colors: 'Colors',
    fonts: 'Fonts',
    sizes: 'Sizes',
    dimensions: 'Dimensions',
    positions: 'Positions',
    alignments: 'Alignments',
    margins: 'Margins',
    padding: 'Padding',
    borders: 'Borders',
    shadows: 'Shadows',
    effects: 'Effects',
    animations: 'Animations',
    transitions: 'Transitions',
    interactions: 'Interactions',
    gestures: 'Gestures',
    shortcuts: 'Shortcuts',
    hotkeys: 'Hotkeys',
    commands: 'Commands',
    functions: 'Functions',
    features: 'Features',
    capabilities: 'Capabilities',
    tools: 'Tools',
    utilities: 'Utilities',
    widgets: 'Widgets',
    components: 'Components',
    modules: 'Modules',
    plugins: 'Plugins',
    extensions: 'Extensions',
    addons: 'Add-ons',
    integrations: 'Integrations',
    connections: 'Connections',
    apis: 'APIs',
    services: 'Services',
    applications: 'Applications',
    software: 'Software',
    hardware: 'Hardware',
    devices: 'Devices',
    platforms: 'Platforms',
    systems: 'Systems',
    frameworks: 'Frameworks',
    libraries: 'Libraries',
    packages: 'Packages',
    dependencies: 'Dependencies',
    requirements: 'Requirements',
    specifications: 'Specifications',
    standards: 'Standards',
    protocols: 'Protocols',
    schemas: 'Schemas',
    structures: 'Structures',
    models: 'Models',
    entities: 'Entities',
    objects: 'Objects',
    items: 'Items',
    elements: 'Elements',
    parts: 'Parts',
    pieces: 'Pieces',
    fragments: 'Fragments',
    sections: 'Sections',
    chapters: 'Chapters',
    pages: 'Pages',
    articles: 'Articles',
    posts: 'Posts',
    entries: 'Entries',
    records: 'Records',
    logs: 'Logs',
    history: 'History',
    timeline: 'Timeline',
    schedule: 'Schedule',
    calendar: 'Calendar',
    events: 'Events',
    appointments: 'Appointments',
    meetings: 'Meetings',
    conferences: 'Conferences',
    calls: 'Calls',
    messages: 'Messages',
    emails: 'Emails',
    reminders: 'Reminders',
    tasks: 'Tasks',
    todos: 'To-dos',
    projects: 'Projects',
    workflows: 'Workflows',
    processes: 'Processes',
    procedures: 'Procedures',
    steps: 'Steps',
    stages: 'Stages',
    phases: 'Phases',
    milestones: 'Milestones',
    deadlines: 'Deadlines',
    goals: 'Goals',
    objectives: 'Objectives',
    targets: 'Targets',
    achievements: 'Achievements',
    results: 'Results',
    outcomes: 'Outcomes',
    outputs: 'Outputs',
    inputs: 'Inputs',
    resources: 'Resources',
    assets: 'Assets',
    materials: 'Materials',
    supplies: 'Supplies',
    equipment: 'Equipment',
    instruments: 'Instruments',
    machines: 'Machines',
    vehicles: 'Vehicles',
    facilities: 'Facilities',
    buildings: 'Buildings',
    rooms: 'Rooms',
    spaces: 'Spaces',
    areas: 'Areas',
    zones: 'Zones',
    done: 'Done',
    undone: 'Undone',
    accomplished: 'Accomplished',
    unaccomplished: 'Unaccomplished'
  },

  // Navigation
  navigation: {
    home: 'Home',
    dashboard: 'Dashboard',
    inventory: 'Inventory',
    orders: 'Orders',
    customers: 'Customers',
    suppliers: 'Suppliers',
    purchases: 'Purchases',
    analytics: 'Analytics',
    reports: 'Reports',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    help: 'Help',
    support: 'Support'
  },

  // Authentication
  auth: {
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    forgotPassword: 'Forgot Password',
    resetPassword: 'Reset Password',
    changePassword: 'Change Password',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    rememberMe: 'Remember Me',
    loginButton: 'Sign In',
    registerButton: 'Sign Up',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    signInHere: 'Sign in here',
    signUpHere: 'Sign up here',
    invalidCredentials: 'Invalid email or password',
    accountCreated: 'Account created successfully',
    passwordResetSent: 'Password reset link sent to your email',
    passwordChanged: 'Password changed successfully',
    login_page: {
      title: 'The enterprise operating system.',
      features: {
        speed: { title: 'Local Speed', desc: 'High-performance architecture built for the Indian marketplace.' },
        trust: { title: 'Encrypted Trust', desc: 'Bank-grade security protocols for your sensitive financial data.' }
      },
      serving: 'Serving 10,000+ Indian Enterprises',
      welcome: 'Welcome back.',
      subtitle: 'Authorized access only. Please verify your credentials.',
      email_label: 'Email Architecture',
      password_label: 'Security Key',
      reset_access: 'Reset Access',
      submit: 'Initialize Dashboard',
      verifying: 'Verifying Credentials...',
      third_party: 'Third Party Auth',
      verify_google: 'Verify with Google',
      no_account: 'No account?',
      open_account: 'Open Enterprise Account',
      subtitle_ca: 'Secure portal access for Chartered Accountants.',
      login_as_vendor: 'Login as Business',
      login_as_ca: 'Login as CA',
      email_label_ca: 'CA ID / Email',
      submit_ca: 'Access Client Portal'
    },
    register_page: {
      title: 'Join the elite tier.',
      features: {
        gst: { title: 'GST Optimized', desc: 'Enterprise-grade compliance engine.' },
        sync: { title: 'Atomic Sync', desc: 'Instant data propagation across devices.' }
      },
      testimonial: '"Soma is the infrastructure we needed to scale from 2 to 20 locations."',
      testimonial_author: 'Rajesh Kumar',
      testimonial_role: 'Owner, Global Traders',
      form_title: 'Open Account.',
      form_subtitle: 'Secure your business architecture with an elite 14-day trial.',
      labels: {
        first_name: 'First Name',
        last_name: 'Last Name',
        email: 'Corporate Email',
        password: 'Security Key',
        confirm_password: 'Confirm Key',
        business_name: 'Business Identity',
        phone: 'Phone Number',
        entity_type: 'Entity Type',
        sector: 'Sector'
      },
      placeholders: {
        select_type: 'Select Type',
        select_industry: 'Select Industry'
      },
      submit: 'Request Access',
      already_registered: 'Already registered?',
      sign_in: 'Authorized Sign In'
    }
  },

  // Business Management
  // business: {
  //   name: 'Business Name',
  //   type: 'Business Type',
  //   category: 'Category',
  //   description: 'Description',
  //   address: 'Address',
  //   phone: 'Phone Number',
  //   email: 'Email Address',
  //   website: 'Website',
  //   gstNumber: 'GST Number',
  //   panNumber: 'PAN Number',
  //   bankAccount: 'Bank Account',
  //   ifscCode: 'IFSC Code',
  //   accountNumber: 'Account Number',
  //   accountHolder: 'Account Holder Name'
  // },

  // Inventory Management
  inventory: {
    items: 'Items',
    item: 'Item',
    product: 'Product',
    service: 'Service',
    sku: 'SKU',
    barcode: 'Barcode',
    category: 'Category',
    brand: 'Brand',
    unit: 'Unit',
    quantity: 'Quantity',
    price: 'Price',
    cost: 'Cost',
    sellingPrice: 'Selling Price',
    purchasePrice: 'Purchase Price',
    stock: 'Stock',
    lowStock: 'Low Stock',
    outOfStock: 'Out of Stock',
    inStock: 'In Stock',
    stockAlert: 'Stock Alert',
    reorderLevel: 'Reorder Level',
    maxStock: 'Maximum Stock',
    minStock: 'Minimum Stock',
    addItem: 'Add Item',
    editItem: 'Edit Item',
    deleteItem: 'Delete Item',
    bulkImport: 'Bulk Import',
    bulkExport: 'Bulk Export',
    stockUpdate: 'Stock Update',
    stockAdjustment: 'Stock Adjustment',
    stockTransfer: 'Stock Transfer'
  },

  // Time formatting
  time: {
    justNow: 'Just now',
    minutesAgo: '{{count}} minutes ago',
    hoursAgo: '{{count}} hours ago',
    daysAgo: '{{count}} days ago',
    weeksAgo: '{{count}} weeks ago',
    monthsAgo: '{{count}} months ago',
    yearsAgo: '{{count}} years ago'
  },

  // Pluralization
  items: {
    one: '{{count}} item',
    other: '{{count}} items'
  },

  customers: {
    one: '{{count}} customer',
    other: '{{count}} customers'
  },

  orders: {
    one: '{{count}} order',
    other: '{{count}} orders'
  },
  
  // Mobile app translations merged
  dashboard: {
    dashboard: "Dashboard",
    business_overview: "Business Overview",
    action_required: "Action Required",
    profile_incomplete_message: "Your business profile is incomplete. Please provide the necessary details to continue.",
    complete_profile: "Complete Profile",
    quick_actions: "Quick Actions",
    add_sale: "Add Sale",
    manage_stock: "Manage Stock",
    view_orders: "View Orders",
    todays_overview: "Today's Overview",
    weeks_overview: "This Week's Overview",
    months_overview: "This Month's Overview",
    low_stock: "Low Stock",
    business_management: "Business Management",
    inventory_management_subtitle: "Manage products and stock",
    order_management_subtitle: "Track and fulfill orders",
    purchase_management_subtitle: "Track and manage purchases",
    customer_management_subtitle: "Manage customer relationships",
    supplier_management_subtitle: "Manage supplier relationships",
    business_analytics_subtitle: "View performance metrics",
    welcome_back: "Welcome back",
    orders_need_attention: "Orders Need Attention",
    total_revenue: "Total Revenue",
    active_orders: "Active Orders",
    stock_value: "Stock Value",
    total_customers: "Total Customers",
    live: "Live",
    monthly_sales: "Monthly Sales",
    view_details: "View Details",
    recent_orders: "Recent Orders",
    view_all: "View All",
    order_id: "Order ID",
    customer: "Customer",
    amount: "Amount",
    status: "Status",
    important_tasks: "Important Tasks",
    in_stock: "in stock",
    buy_more: "Buy More",
    manage_inventory: "Manage Inventory",
    goals: "Goals",
    sales_goal: "Sales Goal",
    revenue_goal: "Revenue Goal",
    search_placeholder: "Search orders, customers or products...",
    loading: "Loading..."
  },
  analytics: {
    title: "Analytics",
    subtitle: "View business reports and forecasts",
    last_7_days: "Last 7 Days",
    last_30_days: "Last 30 Days",
    last_3_months: "Last 3 Months",
    business_reports: "Business Reports",
    sales_forecast: "Sales Forecast",
    reports_library: "Reports Library",
    total_revenue: "Total Revenue",
    average_order_value: "Average Order Value",
    growth_rate: "Growth Rate",
    total_orders: "Total Orders",
    sales_by_channel: "Sales by Channel",
    sales_breakdown: "Sales breakdown by channel",
    online_sales: "Online Sales",
    store_sales: "Store Sales",
    wholesale_sales: "Wholesale Sales",
    ai_suggestions: "AI Suggestions",
    ai_insight_1: "Your average order value increased by 15% this quarter.",
    ai_insight_1_desc: "New premium features have increased sales, contributing to higher revenue.",
    next_month: "Next Month",
    next_3_months: "Next 3 Months",
    accuracy: "Accuracy",
    sales_growth: "Sales Growth",
    sales_growth_subtitle: "Sales forecast for upcoming months",
    search_reports: "Search report templates...",
    reports_available: "Reports Available",
    no_reports: "No Reports",
    no_reports_desc: "Your reports will appear here",
    report_name: "Report Name",
    system_default: "System Default",
    description: "Description",
    actions: "Actions",
    edit: "Edit",
    generate: "Generate"
  },
    business_settings_page: {
      business_settings: "Business Settings",
      manage_business_preferences: "Manage your business preferences and configurations",
      general_settings: "General Settings",
      configure_general_settings: "Configure basic business settings",
      language: "Language",
      currency: "Currency",
      select_currency: "Select Currency",
      timezone: "Timezone",
      select_timezone: "Select Timezone",
      notifications: "Notifications",
      manage_notification_preferences: "Manage your notification preferences",
      low_stock_alerts: "Low Stock Alerts",
      low_stock_description: "Get notified when inventory runs low",
      expiring_items: "Expiring Items",
      expiring_items_description: "Get notified about items nearing expiration",
      order_updates: "Order Updates",
      order_updates_description: "Get notified about new orders and status changes",
      saving: "Saving...",
      save_changes: "Save Changes",
      failed_to_load_settings: "Failed to load settings",
      settings_saved_successfully: "Settings saved successfully",
      failed_to_save_settings: "Failed to save settings"
    },

  business: {
    profile: "Business Profile",
    edit_profile: "Edit Profile",
    create_profile: "Create Business Profile",
    edit_business_profile: "Edit Business Profile",
    create_business_profile: "Create Business Profile",
    no_profile_found: "No Business Profile Found",
    no_business_profile: "No Business Profile Found",
    no_business_profile_description: "You haven't set up your business profile yet. Create one to unlock all features.",
    go_back: "Go Back",
    basic_info: "Basic Information",
    business_name: "Business Name",
    enter_name: "Enter business name",
    business_type: "Business Type",
    select_type: "Select business type",
    email: "Email",
    enter_email: "Enter email address",
    phone: "Phone",
    enter_phone: "Enter phone number",
    address: "Address",
    enter_address: "Enter complete address",
    not_provided: "Not provided",
    fetch_profile_error: "Could not fetch business profile",
    flower_vendor: "Flower Vendor",
    grocery_store: "Grocery Store",
    textile_shop: "Textile Shop",
    hardware_store: "Hardware Store",
    electronics_shop: "Electronics Shop",
    other: "Other",
    business_label: "Business",
    category: "Category",
    retailer: "Retailer",
    wholesaler: "Wholesaler",
    manufacturer: "Manufacturer",
    service_provider: "Service Provider",
    annual_turnover: "Annual Turnover",
    turnover_range_0_5L: "₹0 - ₹5 Lakhs",
    turnover_range_5L_20L: "₹5 Lakhs - ₹20 Lakhs",
    turnover_range_20L_1Cr: "₹20 Lakhs - ₹1 Crore",
    turnover_range_1Cr_plus: "₹1 Crore+",
    gst_registered: "GST Registered",
    yes: "Yes",
    no: "No",
    gst_number: "GST Number",
    registration_date: "Registration Date",
    pick_date: "Pick a date",
    gst_date_required: "GST Registration Date is required",
    n_a: "N/A",
    fetch_gst_error: "Failed to fetch GST settings",
    toggle_gst_error: "Failed to toggle GST features",
    update_gst_status_error: "Failed to update GST status",
    save_error: "Failed to save changes",
    profile_updated: "Profile updated successfully",
    error_saving: "An error occurred while saving",
    business_profile_not_found: "Business profile not found.",
    gst_configuration: "GST Configuration",
    enabled: "Enabled",
    disabled: "Disabled",
    gstin_number: "GSTIN Number",
    gst_registration_date: "GST Registration Date",
    default_cgst: "Default CGST (%)",
    default_sgst: "Default SGST (%)",
    default_igst: "Default IGST (%)",
    auto_sync: "Auto-sync invoices with GST portal",
    cancel: "Cancel",
    saving: "Saving...",
    save_changes: "Save Changes",
    placeholders: {
      enter_business_name: "Enter business name",
      select_business_type: "Select business type",
      enter_email: "business@example.com",
      enter_phone: "+91 98765 43210",
      enter_address: "Enter complete address",
      select_category: "Select category",
      select_turnover_range: "Select turnover range",
      enter_gstin: "22AAAAA0000A1Z5"
    }
  },

  settings: {
    settingsTitle: "Settings",
    inventorySection: "Inventory",
    lowStockThresholdLabel: "Low Stock Threshold",
    lowStockThresholdDescription: "Minimum stock level before an alert is triggered.",
    stockAlertsLabel: "Stock Alerts",
    stockAlertsDescription: "Receive notifications for low stock.",
    autoReorderLabel: "Auto Re-order",
    autoReorderDescription: "Automatically generate purchase orders when stock is low.",
    ordersSection: "Orders",
    autoConfirmOrdersLabel: "Auto-confirm Orders",
    autoConfirmOrdersDescription: "Automatically approve and process incoming orders.",
    defaultPaymentMethodLabel: "Default Payment Method",
    defaultPaymentMethodDescription: "Set a default payment method for new orders.",
    orderNumberPrefixLabel: "Order Number Prefix",
    orderNumberPrefixDescription: "A custom prefix for all order numbers (e.g., 'ORD-').",
    invoicingSection: "Invoicing",
    autoGenerateInvoicesLabel: "Auto-generate Invoices",
    autoGenerateInvoicesDescription: "Generate a new invoice automatically for each order.",
    invoiceNumberPrefixLabel: "Invoice Number Prefix",
    invoiceNumberPrefixDescription: "A custom prefix for all invoice numbers (e.g., 'INV-').",
    defaultTaxRateLabel: "Default Tax Rate (%)",
    defaultTaxRateDescription: "The default tax rate applied to new invoices.",
    paymentTermsLabel: "Payment Terms",
    paymentTermsDescription: "Default payment terms for invoices (e.g., 'Net 30').",
    reportingSection: "Reporting",
    autoEmailReportsLabel: "Email Reports Automatically",
    autoEmailReportsDescription: "Send scheduled reports to your email address.",
    defaultReportPeriodLabel: "Default Report Period",
    defaultReportPeriodDescription: "Set a default period for all reports (e.g., 'Monthly', 'Weekly').",
    reportFormatLabel: "Report Format",
    reportFormatDescription: "Choose the default file format for reports (e.g., 'PDF', 'CSV').",
    loadSettingsError: "Failed to load settings: {{message}}",
    updateFailedError: "Update Failed",
    invalidInputError: "Invalid Input",
    enterValidNumberError: "Please enter a valid number.",
    selectOption: "Select",
    paymentMethodCash: "Cash",
    paymentMethodCard: "Card",
    paymentMethodUPI: "UPI",
    paymentMethodBankTransfer: "Bank Transfer",
    reportPeriodDaily: "Daily",
    reportPeriodWeekly: "Weekly",
    reportPeriodMonthly: "Monthly",
    reportPeriodQuarterly: "Quarterly",
    reportFormatPDF: "PDF",
    reportFormatExcel: "Excel",
    reportFormatCSV: "CSV",
    enterValuePlaceholder: "Enter value"
  },
  businessProfile: {
    business_profile: "Business Profile",
    no_business_profile: "No Business Profile",
    no_business_profile_description: "It seems you haven't set up your business profile yet.",
    create_business_profile: "Create Business Profile",
    flower_vendor: "Flower Vendor",
    grocery_store: "Grocery Store",
    textile_shop: "Textile Shop",
    hardware_store: "Hardware Store",
    electronics_shop: "Electronics Shop"
  },
  businessSettings: {
    businessSettings: "Business Settings",
    currency: "Currency",
    setCurrency: "Set the default currency for your business.",
    language: "Language",
    setLanguage: "Set the language for your account.",
    timezone: "Timezone",
    setTimezone: "Set the timezone for your business.",
    notifications: "Notifications",
    lowStockAlerts: "Low Stock Alerts",
    lowStockDescription: "Receive alerts for low stock items.",
    expiringItems: "Expiring Items",
    expiringItemsDescription: "Receive alerts for items nearing their expiry date.",
    orderUpdates: "Order Updates",
    orderUpdatesDescription: "Receive alerts for new orders and order status changes.",
    select: "Select",
    updateFailed: "Update Failed",
    loadingError: "Failed to load settings",
    inr: "Indian Rupee (INR)",
    usd: "US Dollar (USD)",
    en: "English",
    hi: "Hindi",
    te: "Telugu",
    ta: "Tamil",
    kn: "Kannada",
    ml: "Malayalam",
    kolkata: "Asia/Kolkata"
  },
  profile: {
    profile: "Profile",
    manage_your_account_settings: "Manage your account settings",
    active: "Active",
    business: "Business",
    business_profile: "Business",
    business_settings: "Business Settings",
    subscription: "Subscription",
    reports_and_analytics: "Reports & Analytics",
    account: "Account",
    edit_profile: "Edit Profile",
    security_and_privacy: "Security & Privacy",
    settings: "Settings",
    preferences: "Preferences",
    advance_view: "Advanced View",
    help_and_support: "Help & Support",
    about_soma: "About SOMA",
    sign_out: "Sign Out",
    logout: "Logout",
    are_you_sure_you_want_to_logout: "Are you sure you want to logout?"
  },
  profile_settings: {
    title: "Settings",
    description: "Manage your account and platform preferences.",
    my_profile: "My Profile",
    my_profile_desc: "Personal information and avatar",
    preferences: "Preferences",
    preferences_desc: "Theme, language, and notifications",
    preferences_page_desc: "Customize your experience on the platform.",
    security_privacy: "Security & Privacy",
    security_privacy_desc: "Password and access security",
    active_sessions: "Active Sessions",
    active_sessions_desc: "Manage logged-in devices",
    sign_out: "Sign Out",
    profile_details: "Profile Details",
    profile_details_desc: "Update your photo and personal information.",
    verified_account: "Verified Account",
    premium_plan: "Premium Plan",
    first_name: "First Name",
    last_name: "Last Name",
    email_address: "Email Address",
    save_profile_changes: "Save Profile Changes",
    saving: "Saving...",
    preferences_title: "Preferences",
    appearance: "Appearance",
    theme: "Theme",
    select_theme: "Select Theme",
    light_mode: "Light Mode",
    dark_mode: "Dark Mode",
    system_default: "System Default",
    display_language: "Display Language",
    select_language: "Select Language",
    notifications: "Notifications",
    notifications_desc: "Manage how you receive updates",
    channels: "Communication Channels",
    activity_alerts: "Activity Alerts",
    security_alerts: "Security Alerts",
    security_alerts_desc: "Notify me of new logins and password changes",
    email_notifications: "Email Notifications",
    email_notifications_desc: "Receive updates via email",
    push_notifications: "Push Notifications",
    push_notifications_desc: "Receive browser notifications",
    sms_notifications: "SMS Notifications",
    sms_notifications_desc: "Receive updates via SMS",
    save_preferences: "Save Preferences",
    security_privacy_header: "Security & Privacy",
    security_privacy_header_desc: "Strengthen your account with a secure password.",
    password_label: "Password",
    current_password: "Current Password",
    new_password: "New Password",
    confirm_new_password: "Confirm New Password",
    update_password: "Update Password",
    active_sessions_header: "Active Sessions",
    active_sessions_header_desc: "Manage devices where your account is currently logged in.",
    this_device: "This Device",
    advanced_security: "Advanced Security",
    advanced_security_desc: "Require multi-factor authentication for all login attempts to this account.",
    enable_2fa: "Enable 2FA Authentication",
    customization_underway: "Customization Underway",
    customization_underway_desc: "We're building granular notification controls and device management to give you total control over your experience."
  },
  security_page: {
    hero_badge: "Security",
    hero_title: "Bank-Grade Security",
    hero_subtitle: "for Your Business.",
    hero_desc: "Your business data is protected with enterprise-grade security measures. We take security seriously and continuously invest in keeping your data safe.",
    measures_title: "Our Security Measures",
    measures_desc: "Multiple layers of security protect your data at every level.",
    compliance_title: "Compliance & Certifications",
    compliance_desc: "We maintain the highest standards of compliance and security certifications.",
    best_practices_title: "Security Best Practices",
    best_practices_desc: "Help us keep your account secure by following these best practices:",
    reporting_title: "Reporting Security Issues",
    reporting_desc: "If you discover a security vulnerability or have concerns about security, please report it to us immediately.",
    responsible_disclosure: "Responsible Disclosure",
    responsible_disclosure_desc: "We appreciate responsible disclosure of security vulnerabilities. Please email security details to:",
    contact_team: "Contact Security Team",
    view_privacy: "View Privacy Policy",
    priority_title: "Your Security is Our Priority",
    priority_desc: "We continuously invest in security infrastructure and best practices to protect your business data. Have questions about security? We're here to help.",
    feature_encryption: "End-to-End Encryption",
    feature_encryption_desc: "All data is encrypted using AES-256 encryption both in transit (TLS 1.3) and at rest. Your business data is protected with bank-grade security standards.",
    feature_audits: "Regular Security Audits",
    feature_audits_desc: "We conduct regular security audits, penetration testing, and vulnerability assessments to identify and address potential security issues proactively.",
    feature_access: "Access Controls",
    feature_access_desc: "Role-based access control (RBAC) ensures that users only have access to the data and features they need. Multi-factor authentication (MFA) is available for enhanced security.",
    feature_backups: "Data Backups",
    feature_backups_desc: "Automated daily backups ensure your data is safe. Backups are encrypted and stored in geographically distributed data centers for redundancy and disaster recovery.",
    feature_compliance: "Compliance & Certifications",
    feature_compliance_desc: "We comply with the Digital Personal Data Protection Act (DPDP Act) and maintain SOC 2 Type II compliance. Regular compliance audits ensure we meet the highest standards.",
    feature_incident: "Incident Response",
    feature_incident_desc: "Our security team monitors systems 24/7 and has a comprehensive incident response plan. We notify affected users promptly in the event of any security incident.",
    practice_passwords: "Use strong, unique passwords for your Soma account",
    practice_mfa: "Enable multi-factor authentication (MFA) for added security",
    practice_access: "Regularly review and update user access permissions",
    practice_contact: "Keep your contact information up to date for security notifications",
    practice_logout: "Log out from shared or public computers",
    practice_report: "Report any suspicious activity immediately to security@soma.com",
    comp_dpdp: "DPDP Act Compliance",
    comp_dpdp_desc: "Full compliance with India's Digital Personal Data Protection Act",
    comp_soc2: "SOC 2 Type II",
    comp_soc2_desc: "Certified for security, availability, and confidentiality",
    comp_iso: "ISO 27001",
    comp_iso_desc: "Information security management system certified",
    comp_gst: "GST Compliance",
    comp_gst_desc: "Secure handling of tax data per Indian regulations",
    compliance_disclaimer: "* Security standards and certifications are part of our development roadmap and compliance goals.",
    reporting_ack: "We will acknowledge receipt within 24 hours and work with you to address the issue promptly."
  },
  subscription_page: {
    subscription: "Subscription",
    manage_subscription: "Manage your subscription plan and billing details.",
    current_plan: "Current Plan",
    gst_settings: "GST Settings",
    gst_settings_desc: "Manage your GST settings and enable GST features for your business compliance and invoicing.",
    available_plans: "Available Plans",
    plan_name: "Plan Name",
    free_tier: "Free Tier",
    included_features: "Included Features",
    no_active_subscription: "No active subscription found.",
    loading: "Loading...",
    configure_gst: "Configure GST",
    processing: "Processing...",
    upgrade_plan: "Upgrade Plan",
    error_fetch_subscription: "Failed to fetch subscription details.",
    error_generic_subscription: "An error occurred while fetching subscription details.",
    error_select_plan: "Please select a plan to upgrade.",
    success_upgrade: "Subscription upgraded successfully!",
    error_upgrade: "Failed to upgrade subscription.",
    error_upgrade_generic: "An error occurred while upgrading subscription.",
    error_business_not_found: "Business profile not found. Please create one first.",
    error_fetch_business: "Failed to fetch business details."
  },

  placeholders: {
      select_language: "Select Language",
      select_timezone: "Select Timezone",
      enter_business_name: "Enter business name",
      select_business_type: "Select business type",
      enter_email: "business@example.com",
      enter_phone: "+91 98765 43210",
      enter_address: "Enter complete address",
      select_category: "Select category",
      select_turnover_range: "Select turnover range",
      gstin_placeholder: "22AAAAA0000A1Z5"
    },
    suppliers_page: {
      suppliers: "Suppliers",
      manage_suppliers: "Manage your suppliers and purchases",
      add_new_supplier: "Add New Supplier",
      total_suppliers: "Total Suppliers",
      total_purchases: "Total Purchases",
      active_suppliers: "Active Suppliers",
      success_rate: "Success Rate",
      search_suppliers: "Search by name or contact person...",
      no_suppliers: "No Suppliers",
      edit_supplier: "Edit Supplier",
      delete_supplier: "Delete Supplier",
      supplier_name: "Supplier Name",
      contact_person: "Contact Person",
      phone: "Phone",
      email: "Email",
      gstin: "GSTIN",
      address: "Address",
      cancel: "Cancel",
      save: "Save",
      delete_confirmation: "Are you sure you want to delete this supplier?",
      actions: "Actions",
      add_supplier: "Add Supplier",
      update_supplier: "Update Supplier",
      supplier_details: "Supplier Details",
      status: "Status",
      general_liaison: "General Liaison",
      terms_net: "Terms: Net",
      no_suppliers_desc: "No suppliers found"
    },
    orders_page: {
      title: "Orders",
      description: "Manage all your orders",
      create_new_order: "Create New Order",
      total_orders: "Total Orders",
      pending_orders: "Pending Orders",
      completed_orders: "Completed Orders",
      direct_sales: "Direct Sales",
      search_placeholder: "Search by order ID or customer name...",
      filter_all: "All",
      filter_pending: "Pending",
      filter_completed: "Completed",
      no_orders: "No Orders",
      no_orders_desc: "No orders found",
      delete_confirmation: "Delete this order?",
      col_order_id: "Order ID",
      col_customer_name: "Customer Name",
      guest: "Guest",
      col_order_status: "Order Status",
      standard: "Standard",
      col_amount: "Amount",
      total: "Total",
      col_actions: "Actions",
      wire: "Wire",
      error_loading_sample: "Loading... showing sample data"
    },
    gst_page: {
      tax_gst: "Tax & GST",
      manage_gst: "Manage your GST returns and tax compliance",
      export_report: "Export Tax Report",
      input_tax_credit: "Input Tax Credit",
      tax_liability: "Tax Liability",
      pending_returns: "Pending Returns",
      compliance_score: "Compliance Score",
      gstin_check: "GSTIN Check",
      file_returns: "File Returns",
      gst_returns: "GST Returns",
      auto_file_gst: "Auto File GST",
      past_returns: "Past Returns",
      automated_filing: "Automated Filing",
      automated_filing_desc: "Automatically generate and file returns based on your invoice data.",
      gstr1_sales: "GSTR-1 (Sales)",
      feature_auto_pop: "Feature: Auto-population from invoices",
      auto_file_label: "Auto-File",
      gstr3b_summary: "GSTR-3B (Summary)",
      feature_liability: "Feature: Tax liability summary",
      gst_verified: "GST Verified",
      verified_successfully: "Verified Successfully",
      invalid_gst_number: "Invalid GST Number",
      gst_not_valid: "The GST number provided is not valid.",
      business_name: "Business Name",
      state: "State",
      maharashtra: "Maharashtra",
      enter_gst_number: "Enter GST Number",
      check_gst_number: "Check GST Number",
      checking: "Checking...",
      verify_gst_desc: "Verify your GSTIN instantly",
      verify_gst_subdesc: "Ensure your business is compliant by verifying your GST number against the official government database.",
      gstin_verification: "GSTIN Verification",
      coming_soon: "Coming Soon",
      history_coming_soon: "Filing history and past returns will be available here soon.",
      return_type: "Return Type",
      period: "Period",
      due_date: "Due Date",
      submit_return: "Submit Return",
      due: "Due",
      filed: "Filed",
      all_returns_filed: "All Returns Filed",
      returns_up_to_date: "You have no pending returns to file.",
    },
    profile_settings: {
      phone_number: "Phone Number"
    },
  customers_page: {
    customers: "Customers",
    manage_customers: "Manage your customers and payments",
    add_new_customer: "Add New Customer",
    total_customers: "Total Customers",
    paid_customers: "Paid Customers",
    pending_payments: "Pending Payments",
    total_due: "Total Due",
    search_placeholder: "Search by name, email or phone...",
    all: "All",
    paid: "Paid",
    due: "Due",
    no_customers: "No Customers",
    no_customers_desc: "No customers found",
    customer_name: "Customer Name",
    id: "ID",
    contact_details: "Contact Details",
    current_status: "Current Status",
    pending_payment: "Pending Payment",
    amount_due: "Amount Due",
    actions: "Actions",
    delete_confirmation: "Delete this customer?",
    status_pending: "Pending Payment",
    status_no_dues: "No Dues",
    last_active: "Last active"
  },
  purchases_page: {
    purchases: "Purchases",
    manage_purchases: "Manage your purchases and payments",
    add_purchase: "Add Purchase",
    total_purchases: "Total Purchases",
    total_spent: "Total Spent",
    pending_purchases: "Pending Purchases",
    pending_payment: "Pending Payment",
    search_placeholder: "Search by supplier name or purchase ID...",
    all: "All",
    pending: "Pending",
    unpaid: "Unpaid",
    no_purchases: "No Purchases",
    no_purchases_desc: "No purchases found",
    id_date: "ID / Date",
    supplier: "Supplier",
    amount: "Amount",
    before_tax: "Before Tax",
    status: "Status",
    payment_status: "Payment Status",
    actions: "Actions",
    delete_confirmation: "Delete this purchase?",
    paid: "Paid",
    partial: "Partial"
  },
  inventory_page: {
    inventory: "Inventory",
    manage_inventory: "Manage your products and stock levels",
    add_product: "Add Product",
    total_items: "Total Items",
    low_stock: "Low Stock",
    out_of_stock: "Out of Stock",
    stock_value: "Stock Value",
    search_placeholder: "Search products by code, name or type...",
    all_items: "All Items",
    product_name: "Product Name",
    product_code: "Product Code",
    stock_status: "Stock Status",
    price: "Price",
    before_tax: "Before Tax",
    actions: "Actions",
    in_stock: "In Stock",
    units: "units",
    delete_confirmation: "Delete this product?",
    no_products: "No Products",
    no_products_desc: "No products found",
    edit_product: "Edit Product",
    delete_product: "Delete Product",
    loading_sample_data: "Loading... Showing sample data"
  },
  billing_page: {
    title: "Financials & Billing",
    subtitle: "Architecture for corporate subscriptions and resource settlements",
    tabs: {
      subscription: "Subscription",
      history: "History",
      settlements: "Settlements",
      corporate_profile: "Corporate Profile"
    },
    subscription: {
      tier_status: "Tier Status",
      authenticated: "Authenticated",
      renewal: "Renewal: ",
      ascend_plan: "Ascend Plan",
      equilibrium_method: "Equilibrium Method",
      recent_settlement: "Recent Settlement",
      verified: "Verified"
    },
    history: {
      invoice_identifier: "Invoice Identifier",
      issuance_date: "Issuance Date",
      settlement_amount: "Settlement Amount",
      status: "Status",
      actions: "Actions",
      no_history_title: "No settlement history",
      no_history_desc: "Your verified invoices will materialize here post-authentication."
    },
    settlements: {
      architecture_title: "Settlement Architecture",
      architecture_desc: "Authorized corporate access points",
      initialize_method: "Initialize Method",
      no_vectors: "No settlement vectors identified"
    },
    profile: {
      corporate_identity: "Corporate Identity",
      tax_protocol: "Tax Protocol / GSTIN",
      registered_address: "Registered Address",
      metropolis: "Metropolis",
      jurisdiction: "Jurisdiction",
      postal_code: "Postal Code",
      sovereignty: "Sovereignity",
      synchronizing: "Synchronizing...",
      authorize_updates: "Authorize Updates",
      update_success: "Billing information updated successfully"
    }
  },
  locations_page: {
    title: "Locations",
    subtitle: "Manage multiple locations and stock transfers",
    transfer_stock: "Transfer Stock",
    add_location: "Add Location",
    total_locations: "Locations",
    active_locations: "Active Locations",
    total_stock: "Total Stock",
    transfers_progress: "Transfers in Progress",
    tabs: {
      locations: "Locations",
      all_stock: "All Locations Stock",
      transfers: "Stock Transfers",
      analytics: "Analytics"
    },
    table: {
      location_name: "Location Name",
      type: "Type",
      address: "Address",
      contact: "Contact",
      status: "Status",
      actions: "Actions",
      active: "Active",
      inactive: "Inactive",
      store: "Store",
      warehouse: "Warehouse",
      view_details: "View Details",
      edit: "Edit",
      delete: "Delete"
    },
    transfer_table: {
      date: "Transfer Date",
      date_label: "Date",
      route: "From → To"
    },
    empty_state: {
      no_locations: "No Locations Found",
      no_locations_desc: "You haven't added any locations yet. Add a store or warehouse to get started.",
      no_transfers: "No Transfers Found",
      no_transfers_desc: "You haven't initiated any stock transfers yet.",
      no_stock: "No Stock Data",
      no_stock_desc: "No stock data available for the selected location."
    },
    analytics: {
      sales_analysis: "Sales Analysis",
      coming_soon: "Advanced analytics coming soon."
    }
  },
  usage_page: {
    title: "Usage & Limits",
    subtitle: "View your usage and limits",
    export_report: "Export Report",
    cards: {
      api_calls: "API Calls",
      storage_used: "Storage Used",
      data_transfer: "Data Transfer",
      processing_time: "Processing Time"
    },
    tabs: {
      system_status: "System Status",
      usage_limits: "Usage Limits",
      usage_history: "Usage History",
      alerts: "Alerts"
    },
    monitor: {
      system_health: "System Health",
      current_system_status: "Current system status",
      uptime: "Uptime",
      response_time: "Response Time",
      errors: "Errors",
      database_usage: "Database Usage",
      auto_scaling: "Auto Scaling",
      system_running_smoothly: "System is running smoothly.",
      system_running_desc: "System is automatically managing resources based on usage. System is running well for fast performance.",
      manage_auto_scaling: "Manage Auto Scaling"
    },
    quotas: {
      usage_limit: "Usage Limit",
      used: "Used",
      total_limit: "Total Limit"
    },
    history: {
      date: "Date",
      api_calls: "API Calls",
      storage: "Storage",
      data_transfer: "Data Transfer",
      reqs: "reqs",
      no_history: "No History",
      no_history_desc: "Usage history will appear here"
    },
    alerts: {
      fix_issue: "Fix Issue",
      all_systems_good: "All Systems Good",
      no_issues_found: "No issues found"
    }
  },
  profile_settings: {
    title: "Settings",
    description: "Manage your account and platform preferences.",
    my_profile: "My Profile",
    my_profile_desc: "Personal information and avatar",
    notifications: "Notifications",
    notifications_desc: "Manage how you receive updates",
    preferences: "Preferences",
    preferences_desc: "Theme, language, and notifications",
    security_privacy: "Security & Privacy",
    security_privacy_desc: "Password and access security",
    active_sessions: "Active Sessions",
    active_sessions_desc: "Manage logged-in devices",
    preferences_title: "Preferences",
    preferences_page_desc: "Customize your experience on the platform.",
    appearance: "Appearance",
    theme: "Theme",
    select_theme: "Select Theme",
    light_mode: "Light Mode",
    dark_mode: "Dark Mode",
    system_default: "System Default",
    display_language: "Display Language",
    select_language: "Select Language",
    saving: "Saving...",
    save_preferences: "Save Preferences",
    channels: "Communication Channels",
    email_notifications: "Email Notifications",
    email_notifications_desc: "Receive updates via email",
    push_notifications: "Push Notifications",
    push_notifications_desc: "Receive browser notifications",
    sms_notifications: "SMS Notifications",
    sms_notifications_desc: "Receive updates via SMS",
    activity_alerts: "Activity Alerts",
    security_alerts: "Security Alerts",
    security_alerts_desc: "Notify me of new logins and password changes",
    security_privacy_header: "Security & Privacy",
    security_privacy_header_desc: "Strengthen your account with a secure password.",
    current_password: "Current Password",
    new_password: "New Password",
    confirm_password: "Confirm Password",
    updating: "Updating...",
    change_password: "Change Password",
    advanced_security: "Advanced Security",
    advanced_security_desc: "Require multi-factor authentication for all login attempts to this account.",
    enable_2fa: "Enable 2FA",
    customization_underway: "Customization Underway",
    customization_underway_desc: "We're building granular notification controls and device management to give you total control over your experience.",
    premium_plan: "Premium Plan",
    first_name: "First Name",
    last_name: "Last Name",
    email_address: "Email Address",
    save_profile_changes: "Save Profile Changes"
  },
  sidebar: {
    menu: {
      overview: "Overview",
      business: "Business",
      finance: "Finance",
      system: "System"
    },
    items: {
      dashboard: "Dashboard",
      analytics: "Analytics",
      business_profile: "Business",
      inventory: "Inventory",
      orders: "Orders",
      purchases: "Purchases",
      customers: "Customers",
      suppliers: "Suppliers",
      business_settings: "Business Settings",
      tax_gst: "Tax & GST",
      billing: "Billing",
      subscription: "Subscription",
      locations: "Locations",
      settings: "Settings",
      usage: "Usage"
    },
    footer: {
      upgrade_pro: "Upgrade to Pro",
      account: "Account",
      billing: "Billing",
      notifications: "Notifications",
      logout: "Log out"
    }
  }
};